/**
 * Bind live Fencer combat actors to CharacterBody3D scene nodes.
 * Combat remains authoritative; scripts see/update body transforms.
 */

import type { TreeNodeLink } from './ScriptRuntime';
import type { SceneRuntime } from './SceneRuntime';

export type FencerRole = 'player' | 'opponent';

export interface FencerHandle {
  role: FencerRole;
  /** World X on the strip (authoritative combat) */
  getX: () => number;
  setX: (x: number) => void;
  getFacing: () => 1 | -1;
  /** THREE.Object3D root of the fencer (game scene) */
  root: import('three').Object3D;
}

export interface BindingRecord {
  nodeId: string;
  role: FencerRole;
  handle: FencerHandle;
  /** Last body X we wrote from fencer — detect script deltas */
  lastPushedX: number;
  /** Hide placeholder meshes under CharacterBody */
  hidden: import('three').Object3D[];
}

/**
 * Find CharacterBody3D (or packed instance root with character props) for a role.
 */
export function findCharacterNodeId(runtime: SceneRuntime, role: FencerRole): string | null {
  const links = runtime.getAllLinks();
  const candidates: TreeNodeLink[] = [];
  for (const link of links) {
    if (link.data.type !== 'CharacterBody3D') continue;
    candidates.push(link);
  }
  // Prefer explicit isPlayer flag
  for (const link of candidates) {
    const isPlayer = !!link.data.props.isPlayer;
    if (role === 'player' && isPlayer) return link.data.id;
    if (role === 'opponent' && isPlayer === false && link.data.props.isPlayer !== undefined)
      return link.data.id;
  }
  // Groups
  for (const link of candidates) {
    const groups = (link.data.props.groups as string[]) || [];
    const name = link.data.name.toLowerCase();
    if (role === 'player' && (groups.includes('player') || name.includes('player') || name.includes('student')))
      return link.data.id;
    if (
      role === 'opponent' &&
      (groups.includes('ai') || groups.includes('opponent') || name.includes('opponent'))
    )
      return link.data.id;
  }
  // Fallback order: first / second CharacterBody
  if (role === 'player' && candidates[0]) return candidates[0].data.id;
  if (role === 'opponent' && candidates[1]) return candidates[1].data.id;
  if (role === 'opponent' && candidates[0] && candidates.length === 1) {
    // single body that's not player
    if (!candidates[0].data.props.isPlayer) return candidates[0].data.id;
  }
  return null;
}

export class FencerBodyBinder {
  private binds: BindingRecord[] = [];

  clear(runtime: SceneRuntime): void {
    for (const b of this.binds) {
      for (const o of b.hidden) o.visible = true;
      // Restore placeholder visibility; don't reparent game fencer
      void runtime;
    }
    this.binds = [];
  }

  bind(runtime: SceneRuntime, handle: FencerHandle): boolean {
    const nodeId = findCharacterNodeId(runtime, handle.role);
    if (!nodeId) return false;
    const link = runtime.getNodeLink(nodeId);
    if (!link?.object3d) return false;

    // Hide visual placeholders under the CharacterBody (keep collision helpers optional)
    const hidden: import('three').Object3D[] = [];
    link.object3d.traverse((o) => {
      if (o === link.object3d) return;
      const ud = o.userData || {};
      if (ud.collisionShape || ud.area3d) return;
      // Hide meshes that look like placeholders
      if ((o as import('three').Mesh).isMesh || o.type === 'Group' || o.type === 'Object3D') {
        if (o.parent === link.object3d || o.parent?.parent === link.object3d) {
          if (o.visible) {
            o.visible = false;
            hidden.push(o);
          }
        }
      }
    });
    // Simpler: hide all mesh children one level deep that aren't helpers
    link.object3d.children.forEach((c) => {
      if (c.userData?.collisionShape) return;
      if (c.name === 'AnimPlayerMarker') return;
      c.visible = false;
      if (!hidden.includes(c)) hidden.push(c);
    });

    // Mark link as bound fencer
    link.object3d.userData.fencerBound = handle.role;
    link.data.props.groups = Array.from(
      new Set([...(link.data.props.groups || []), handle.role === 'player' ? 'player' : 'ai', 'fencers']),
    );

    const x = handle.getX();
    link.data.transform.position.x = x;
    link.object3d.position.x = x;
    // Face
    link.data.transform.rotation.y = handle.getFacing() > 0 ? 0 : 180;
    link.object3d.rotation.y = (link.data.transform.rotation.y * Math.PI) / 180;

    this.binds.push({
      nodeId,
      role: handle.role,
      handle,
      lastPushedX: x,
      hidden,
    });
    runtime.logBind?.(`Bound ${handle.role} → ${link.data.name} (${nodeId})`);
    return true;
  }

  /**
   * After scripts run: if CharacterBody X moved without us pushing, apply to fencer.
   */
  pullFromBodies(runtime: SceneRuntime): void {
    for (const b of this.binds) {
      const link = runtime.getNodeLink(b.nodeId);
      if (!link) continue;
      const bodyX = link.data.transform.position.x;
      if (Math.abs(bodyX - b.lastPushedX) > 0.0005) {
        b.handle.setX(bodyX);
        b.lastPushedX = bodyX;
      }
    }
  }

  /**
   * After combat update: push fencer state onto CharacterBody for scripts/areas.
   */
  pushToBodies(runtime: SceneRuntime): void {
    for (const b of this.binds) {
      const link = runtime.getNodeLink(b.nodeId);
      if (!link?.object3d) continue;
      const x = b.handle.getX();
      link.data.transform.position.x = x;
      link.object3d.position.x = x;
      // Keep y from scene (usually 0)
      const face = b.handle.getFacing();
      link.data.transform.rotation.y = face > 0 ? 0 : 180;
      link.object3d.rotation.y = (link.data.transform.rotation.y * Math.PI) / 180;
      b.lastPushedX = x;

      // Sync game fencer root world X to match strip (fencer owns its root in game scene)
      b.handle.root.position.x = x;
      b.handle.root.rotation.y = face > 0 ? 0 : Math.PI;
    }
  }

  getBindings(): BindingRecord[] {
    return this.binds.slice();
  }
}
